import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X, Loader } from 'lucide-react';
import type { OrgUnit, AdminUser } from '@schemas/api';
import { useListUsers } from '@api/hooks/useUsers';
import { useAllOrgUnits } from '@api/hooks/useOrgUnits';
import { SearchableSelect } from '@components/common/SearchableSelect';

interface OrgUnitFormProps {
  isOpen: boolean;
  isLoading: boolean;
  unit?: OrgUnit | null;
  parentId?: string | null;
  hierarchyId: string;
  unitTypes: string[];
  onSubmit: (data: FormData) => void;
  onClose: () => void;
}

interface FormData {
  name: string;
  description?: string;
  unit_type: string;
  manager_id?: string;
  hierarchyId?: string;
  parent_id?: string | null;
  unitId?: string;
}

export const OrgUnitForm: React.FC<OrgUnitFormProps> = ({
  isOpen,
  isLoading,
  unit,
  parentId,
  hierarchyId,
  unitTypes,
  onSubmit,
  onClose,
}) => {
  const isEditing = !!unit;

  // Fetch all users for manager selection
  const { data: usersResponse } = useListUsers(0, 100);
  const users = usersResponse?.items || [];

  // Fetch all org units for parent selection
  const { data: allUnits = [] } = useAllOrgUnits();

  // Filter out current unit from parent options to prevent self-reference
  const parentOptions = allUnits.filter(
    (u) => !unit || u.id !== unit.id
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<FormData>({
    defaultValues: {
      name: unit?.name || '',
      description: unit?.description || '',
      unit_type: unit?.unit_type || unitTypes[0] || '',
      manager_id: unit?.manager_id || '',
      parent_id: unit?.parent_id || parentId || null,
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      reset({
        name: unit?.name || '',
        description: unit?.description || '',
        unit_type: unit?.unit_type || unitTypes[0] || '',
        manager_id: unit?.manager_id || '',
        parent_id: unit?.parent_id || parentId || null,
      });
    }
  }, [isOpen, unit, reset, unitTypes, parentId]);

  const handleFormSubmit = (data: FormData) => {
    onSubmit({
      ...data,
      ...(unit && { unitId: unit.id }),
      hierarchyId,
      parent_id: data.parent_id || parentId || null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">
            {isEditing ? 'Edit Unit' : 'Create Unit'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              {...register('name', {
                required: 'Name is required',
                minLength: {
                  value: 1,
                  message: 'Name must not be empty',
                },
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Unit Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <Controller
              name="unit_type"
              control={control}
              rules={{ required: 'Type is required' }}
              render={({ field }) => (
                <SearchableSelect
                  options={[
                    { id: '', label: '-- Select type --' },
                    ...unitTypes.map((type) => ({ id: type, label: type })),
                  ]}
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Select type"
                  disabled={isLoading}
                  clearable={false}
                />
              )}
            />
            {errors.unit_type && (
              <p className="text-xs text-red-600 mt-1">
                {errors.unit_type.message}
              </p>
            )}
          </div>

          {/* Parent Unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent Unit
            </label>
            <Controller
              name="parent_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  options={parentOptions.map((parentUnit: OrgUnit) => ({
                    id: parentUnit.id,
                    label: parentUnit.name,
                    description: parentUnit.unit_type,
                  }))}
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Search for a parent unit..."
                  disabled={isLoading || parentOptions.length === 0}
                  isLoading={isLoading}
                  emptyMessage={parentOptions.length === 0 ? 'Loading units...' : 'No parent units found'}
                />
              )}
            />
            <p className="text-xs text-gray-500 mt-1">
              {parentOptions.length === 0 ? 'Loading units...' : `${parentOptions.length} units available`}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              disabled={isLoading}
              placeholder="Optional description"
            />
          </div>

          {/* Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manager
            </label>
            <Controller
              name="manager_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  options={users.map((user: AdminUser) => ({
                    id: user.id,
                    label: `${user.first_name} ${user.last_name}`,
                    description: user.email,
                  }))}
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Search for a manager..."
                  disabled={isLoading || users.length === 0}
                  isLoading={isLoading}
                  emptyMessage={users.length === 0 ? 'Loading users...' : 'No users found'}
                />
              )}
            />
            <p className="text-xs text-gray-500 mt-1">
              {users.length === 0 ? 'Loading users...' : `${users.length} users available`}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading && <Loader size={16} className="animate-spin" />}
              {isEditing ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
